import { Suspense, lazy } from "react";

import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation } from "react-router";

import { useAuthStore } from "stores/auth";

import ErrorBoundary from "simplydo/react/components/Error/ErrorBoundary";
import { styled } from "simplydo/react/styled";
import { Container } from "simplydo/react/ui/container";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import { useCurrentUser } from "utils/currentUser";

import { StyledDashboardContent } from "components/AppStyledDashboard";
import AppWrap from "components/AppWrap";
import AuthoriseToken from "components/entry/AuthoriseToken";
import { StyledEntry } from "components/entry/Entry";
import LoginError from "components/entry/LoginError";
import LoginWithToken from "components/entry/LoginWithToken";
import SmartLink from "components/entry/SmartLink";
import InnointelCompanyPDF from "components/innointel/CompanyPDF";
import CompanyPDF from "components/innointel/CompanyPDF";
import ReportContent from "components/lib/ReportContent/ReportContent";
import { SIDENAV_FULL_WIDTH_CLASS } from "components/lib/SideNav/SideNavComponent";
import { Footer, NavBar } from "components/lib/UI";

import NonUserSwitch from "./NonUserSwitch";
import SuspenseLoading from "./SuspenseLoading";
import UserSwitch from "./UserSwitch";

const Poll = lazy(() => import("components/polls/Poll"));
const PublicFinalOutcome = lazy(
  () => import("components/finalOutcome/PublicFinalOutcome"),
);

const AppContainer = styled.div`
  display: flex;
  flex: 1 1 0%;
  max-width: 100%;
  min-height: 100%;
  flex-direction: column;
`;

const AppContent = styled.div`
  flex: 1;
  position: relative;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
  padding-bottom: 4rem;

  &:has(> ${StyledDashboardContent}),
  &:has(> ${StyledEntry}),
  &:has(.${SIDENAV_FULL_WIDTH_CLASS}) {
    padding-bottom: 0;
  }
`;

const AppRouting = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const user = useCurrentUser();
  const isGettingUser = useAuthStore((state) => state.isGettingUser);
  const gettingUserError = useAuthStore((state) => state.errorMessage);
  const showLogin = useAuthStore((state) => state.showLogin);

  return (
    <Routes>
      <Route
        path="/report"
        element={
          <AppWrap excludeWelcomeText fluid content={<ReportContent />} />
        }
      />
      <Route
        path="/login/error"
        element={<AppWrap excludeWelcomeText content={<LoginError />} />}
      />
      <Route
        path="/login/with"
        element={<AppWrap excludeWelcomeText content={<LoginWithToken />} />}
      />
      <Route
        path="/start"
        element={<AppWrap excludeWelcomeText content={<SmartLink />} />}
      />
      <Route
        path="/tokens/authorise"
        element={<AppWrap excludeWelcomeText content={<AuthoriseToken />} />}
      />

      <Route
        path="/final-outcome/:token"
        element={
          <AppWrap
            excludeWelcomeText
            fluid
            content={
              <Suspense fallback={<SuspenseLoading />}>
                <PublicFinalOutcome />
              </Suspense>
            }
          />
        }
      />
      <Route
        path="/polls/:id"
        element={
          <AppWrap
            excludeWelcomeText
            fluid
            content={
              <Suspense fallback={<SuspenseLoading />}>
                <Poll />
              </Suspense>
            }
          />
        }
      />

      <Route
        path="/companies/:id/pdf"
        element={<AppWrap excludeWelcomeText fluid content={<CompanyPDF />} />}
      />
      <Route
        path="/innointel/companies/:companyId/pdf"
        element={<InnointelCompanyPDF />}
      />
      <Route
        path="*"
        element={
          isGettingUser ? (
            <Loader style={{ marginTop: 30 }} />
          ) : (
            <AppContainer>
              <NavBar />
              <ErrorBoundary pathName={location.pathname}>
                <AppContent className="app-content">
                  <Suspense fallback={<SuspenseLoading />}>
                    {user && <UserSwitch />}
                    <NonUserSwitch />
                    {gettingUserError && !showLogin ? (
                      <Container style={{ marginTop: 20 }}>
                        <Message>
                          <MessageHeader>
                            {t("main.problemLoading")}
                          </MessageHeader>
                          <MessageContent>{gettingUserError}</MessageContent>
                        </Message>
                      </Container>
                    ) : null}
                  </Suspense>
                </AppContent>
              </ErrorBoundary>
              <Footer />
            </AppContainer>
          )
        }
      />
    </Routes>
  );
};

export default AppRouting;
